/**
 * Скорость воспроизведения в нашем плеере.
 *
 * ЗАЧЕМ. Ученик слушает носителя языка: одному нужно медленнее, чтобы разобрать
 * слова, другому быстрее, чтобы не терять время на знакомом. Родные кнопки
 * YouTube у нас отключены (`controls=0`), поэтому менять скорость было нечем —
 * та же история, что с субтитрами.
 *
 * ⚠️ В ОТЛИЧИЕ ОТ СУБТИТРОВ, СКОРОСТЬ МЕНЯЕТСЯ НА ЖИВОМ ПЛЕЕРЕ.
 * `setPlaybackRate` — документированная часть интерфейса, она работает сразу;
 * пересоздавать плеер не нужно и не надо. Отсюда и разное устройство двух
 * кнопок: субтитры пересобирают плеер, скорость просто просит.
 *
 * Отдельный файл, а не код в компоненте, по той же причине, что и у субтитров:
 * проверки собираются только по образцу `src/**\/*.test.ts`, и логика внутри
 * `.tsx` осталась бы непроверяемой.
 */

/**
 * Набор скоростей. Задан владельцем 09.09.2026.
 *
 * ⚠️ ПОЛОВИННОЙ СКОРОСТИ ЗДЕСЬ НЕТ НАМЕРЕННО: на 0.5 речь превращается в
 * невнятное гудение и разбирать её не легче, а тяжелее. Нижняя граница 0.75 —
 * та, на которой слова ещё звучат словами.
 */
export const SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;

export type Speed = (typeof SPEEDS)[number];

/** Обычная скорость: с неё начинают все, кто ничего не выбирал. */
export const DEFAULT_SPEED: Speed = 1;

/**
 * Привести любое значение к скорости из набора.
 *
 * Нужно на каждой границе, где число приходит извне: из хранилища (его правит
 * кто угодно), из адреса страницы (его правит кто угодно тем более). Чужое
 * значение — это не только мусор вроде `abc`, но и внешне разумные `0.5` и `3`,
 * которых в наборе нет: кнопка не смогла бы показать такое состояние.
 */
export function normalizeSpeed(value: unknown): Speed {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_SPEED;
  return (SPEEDS as readonly number[]).includes(n) ? (n as Speed) : DEFAULT_SPEED;
}

/** Подпись на кнопке: `1×`, `1.25×`. Цифры одинаковы во всех трёх языках. */
export function speedLabel(speed: Speed): string {
  return `${speed}×`;
}

// ---------------------------------------------------------------------------
// Выбор ученика
// ---------------------------------------------------------------------------

/** Рядом с `onebilim:captions` — те же правила, тот же префикс. */
const PREF_KEY = 'onebilim:speed';

/** Записан ли выбор. Нужно там, где умолчание приходит извне — см. `/embed`. */
export function speedPrefStored(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== null;
  } catch {
    return false;
  }
}

export function speedPref(): Speed {
  try {
    return normalizeSpeed(localStorage.getItem(PREF_KEY));
  } catch {
    // Приватный режим браузера — обычная скорость.
    return DEFAULT_SPEED;
  }
}

export function setSpeedPref(speed: Speed): void {
  try {
    localStorage.setItem(PREF_KEY, String(speed));
  } catch {
    /* приватный режим — выбор проживёт до перезагрузки страницы */
  }
}
